/**
 * Creative lab URL state.
 *
 * Home (`/creative-lab`): the Focus app switcher only (no dates, no filters),
 * plus the Ideas | Projects tab and the "Include projects without an app"
 * switch — all in the URL (fixes apps/web's app-filter and tab QUIRKs).
 * Workspace (`/creative-lab/projects/$projectId`): `brief` is apps/web's
 * hand-off prefill. Replacement (`/creative-lab/projects/new`): the app, the
 * asset being replaced, and the client-measured media size.
 */
import { defineFocusConfig, parseFocusSearch, type FocusControls, type FocusSearch } from '@/features/focus';
import { defineSearch, param, type RawSearch } from '@/router/search';

const CONTROLS = { allApps: false, dates: false, filters: false } as const satisfies FocusControls;

export const TABS = ['ideas', 'projects'] as const;
export type Tab = (typeof TABS)[number];

export interface CreativeLabSearch extends FocusSearch {
  /** Absent = Ideas, the default view. */
  tab?: Tab | undefined;
  /** `1`: also list projects that belong to no app. */
  unassigned?: '1' | undefined;
}

export const parseCreativeLabSearch = (raw: RawSearch): CreativeLabSearch => ({
  ...parseFocusSearch(raw, CONTROLS),
  tab: param.enumValue(raw, 'tab', TABS) === 'projects' ? 'projects' : undefined,
  unassigned: param.flag(raw, 'unassigned', '1') ? '1' : undefined,
});

export const creativeLabSearch = defineSearch(parseCreativeLabSearch);

export const CREATIVE_LAB_FOCUS = defineFocusConfig({
  ...CONTROLS,
  parse: parseCreativeLabSearch,
  appSwitcher: {
    evidenceFor: 'insights and ideas',
    hint: 'Click an app to look at it alone, or tick several to see their ideas side by side.',
    portfolioOnPerformance: true,
  },
});

export const workspaceSearch = defineSearch((raw: RawSearch) => ({ brief: param.string(raw, 'brief') }));

export const replacementSearch = defineSearch((raw: RawSearch) => ({
  app_id: param.string(raw, 'app_id'),
  replace: param.string(raw, 'replace'),
  asset_width: param.number(raw, 'asset_width', { min: 1, max: 20_000, integer: true }),
  asset_height: param.number(raw, 'asset_height', { min: 1, max: 20_000, integer: true }),
}));
